import { CompanionCard, ContributorCard } from '@components/Cards';
import { FavoriteIcon } from '@components/Icons/FavoriteIcon';
import { Likedicon } from '@components/Icons/LikedIcon';
import { PlayIcon } from '@components/Icons/PlayIcon';
import NapsterSpinner from '@components/NapsterSpinner';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import useAlbumData from '@hooks/useAlbumData';

import useCategoryFavoritesInfiniteScroll from '@hooks/useCategoryFavoritesInfiniteScroll';
import useContributorsInfiniteScroll from '@hooks/useContributorsInfiniteScroll';
import useFavoriteCompanionsInfiniteScroll from '@hooks/useFavoriteCompanionsInfiniteScroll';
import useTracksInfiniteScroll from '@hooks/useTracksInfiniteScroll';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
//import { useAuth } from 'react-oidc-context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { placeHolderData } from '../../config';
import {
  getTrackById,
  removeFavouriteAlbum as removeFavouriteAlbumAPI,
  removeFavouriteCompanion as removeFavouriteCompanionAPI,
  removeFavouriteContributor as removeFavouriteContributorAPI,
  removeFavouriteTrack as removeFavouriteTrackAPI,
} from '../../services/api';
import { FavoriteAlbumData } from '../../types';
import { getTrackUrlForLocale } from '../../utils/localeUtils';
import { showToast } from '../../utils/toast';
import { FavoriteAlbumCard } from './FavoriteAlbumCard';

type FilterTab =
  | 'all'
  | 'music'
  | 'serenity'
  | 'book'
  | 'tracks'
  | 'contributors'
  | 'companions';

const FILTER_TABS: FilterTab[] = [
  'all',
  'music',
  'serenity',
  'book',
  'tracks',
  'contributors',
  'companions',
];

const isFilterTab = (value: string | null): value is FilterTab =>
  value !== null && FILTER_TABS.includes(value as FilterTab);

export const FavoritesPage = memo(() => {
  const { t } = useTranslation();
  const { setActivePopup } = useCommonState();
  const {
    favouriteAlbums,
    setActivePlaylist,
    addRecentTrack,
    removeFavouriteAlbum,
    removeFavouriteTrack,
    preferredLocale,
  } = useMusicState();
  const { loadAlbumById } = useAlbumData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  //const { user } = useAuth();
  const activeTab = useMemo<FilterTab>(() => {
    const tabFromUrl = searchParams.get('tab');
    return isFilterTab(tabFromUrl) ? tabFromUrl : 'all';
  }, [searchParams]);
  const [unfavoritingItems, setUnfavoritingItems] = useState<Set<string>>(
    new Set(),
  );
  const [initialLoading, setInitialLoading] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleTabClick = useCallback(
    (tab: FilterTab) => {
      if (tab === activeTab) return;
      setSearchParams(
        (prevParams) => {
          const nextParams = new URLSearchParams(prevParams);
          if (tab === 'all') {
            nextParams.delete('tab');
          } else {
            nextParams.set('tab', tab);
          }
          return nextParams;
        },
        { replace: true },
      );
    },
    [activeTab, setSearchParams],
  );

  // Favorites hooks must stay enabled for every category: tab badges read each hook's `total`
  // from the first API page. If we tied `enabled` to `activeTab`, a refresh on e.g. ?tab=companions
  // would leave all other totals at 0 while items still exist. Infinite scroll still only advances
  // when the tab's sentinel is mounted (observer won't fire for hidden sections).
  const musicFavorites = useCategoryFavoritesInfiniteScroll({
    category: 'music',
    rootMargin: '200px',
    scrollContainerRef,
  });

  const serenityFavorites = useCategoryFavoritesInfiniteScroll({
    category: 'serenity',
    rootMargin: '200px',
    scrollContainerRef,
  });

  const wisdomFavorites = useCategoryFavoritesInfiniteScroll({
    category: 'book',
    rootMargin: '200px',
    scrollContainerRef,
  });

  const tracksFavorites = useTracksInfiniteScroll({
    rootMargin: '200px',
    scrollContainerRef,
  });

  const contributorsFavorites = useContributorsInfiniteScroll({
    rootMargin: '200px',
    scrollContainerRef,
  });

  const companionsFavorites = useFavoriteCompanionsInfiniteScroll({
    rootMargin: '200px',
    scrollContainerRef,
  });

  // Custom unfavorite handler with optimistic updates
  const handleUnfavorite = useCallback(
    async (
      type: 'album' | 'track' | 'contributor' | 'companion',
      id: string,
    ) => {
      // Add to unfavoriting set to show loading state
      setUnfavoritingItems((prev) => new Set(prev).add(id));

      // Get album name for toast message before removing
      let albumName: string | undefined;
      if (type === 'album') {
        const album = favouriteAlbums.find(
          (album: FavoriteAlbumData) => album.id === id,
        );
        albumName = album?.name;
      }

      // Optimistic update - remove from Redux store and infinite scroll hooks immediately
      if (type === 'album') {
        removeFavouriteAlbum(id);
        // Also remove from the category-specific hooks
        if (activeTab === 'all' || activeTab === 'music') {
          musicFavorites.removeItem(id);
        }
        if (activeTab === 'all' || activeTab === 'serenity') {
          serenityFavorites.removeItem(id);
        }
        if (activeTab === 'all' || activeTab === 'book') {
          wisdomFavorites.removeItem(id);
        }
      } else if (type === 'track') {
        removeFavouriteTrack(id);
        tracksFavorites.removeItem(id);
      } else if (type === 'contributor') {
        contributorsFavorites.removeItem(id);
      } else if (type === 'companion') {
        companionsFavorites.removeItem(id);
      }

      try {
        // Call the API directly to unfavorite (don't use toggleFavorite to avoid confusion)
        if (type === 'album') {
          await removeFavouriteAlbumAPI(id);
          showToast.unliked(albumName);
        } else if (type === 'track') {
          await removeFavouriteTrackAPI(id);
          showToast.unliked();
        } else if (type === 'contributor') {
          await removeFavouriteContributorAPI(id);
          showToast.unliked();
        } else if (type === 'companion') {
          await removeFavouriteCompanionAPI(id);
          showToast.unliked();
        }
      } catch (error) {
        console.error('Failed to unfavorite item:', error);
        // On error, show error message
        showToast.error(t('favorites.removeFailed'));
      } finally {
        // Remove from unfavoriting set
        setUnfavoritingItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }
    },
    [
      favouriteAlbums,
      removeFavouriteAlbum,
      removeFavouriteTrack,
      activeTab,
      musicFavorites,
      serenityFavorites,
      wisdomFavorites,
      tracksFavorites,
      contributorsFavorites,
      companionsFavorites,
    ],
  );

  // Track initial loading state
  useEffect(() => {
    // Set initial loading to false once we have data or all infinite scroll hooks are done loading
    const hasAnyData =
      musicFavorites.items.length > 0 ||
      serenityFavorites.items.length > 0 ||
      wisdomFavorites.items.length > 0 ||
      tracksFavorites.items.length > 0 ||
      contributorsFavorites.items.length > 0 ||
      companionsFavorites.items.length > 0;

    const allLoadingDone =
      !musicFavorites.loading &&
      !serenityFavorites.loading &&
      !wisdomFavorites.loading &&
      !tracksFavorites.loading &&
      !contributorsFavorites.loading &&
      !companionsFavorites.loading;

    if (hasAnyData || allLoadingDone) {
      setInitialLoading(false);
    }
  }, [
    musicFavorites.items.length,
    musicFavorites.loading,
    serenityFavorites.items.length,
    serenityFavorites.loading,
    wisdomFavorites.items.length,
    wisdomFavorites.loading,
    tracksFavorites.items.length,
    tracksFavorites.loading,
    contributorsFavorites.items.length,
    contributorsFavorites.loading,
    companionsFavorites.items.length,
    companionsFavorites.loading,
  ]);
  const playAlbum = useCallback(
    async (album: FavoriteAlbumData) => {
      try {
        // Fetch complete album data using the album ID
        const fullAlbumData = await loadAlbumById(album.id);

        if (
          !fullAlbumData ||
          !fullAlbumData.tracks ||
          fullAlbumData.tracks.length === 0
        ) {
          console.warn('No tracks found in album:', album.name);
          return;
        }

        const contributor =
          fullAlbumData.contributors && fullAlbumData.contributors.length > 0
            ? fullAlbumData.contributors[0]
            : null;
        const firstTrack = fullAlbumData.tracks[0];

        const playlist = {
          currentIndex: 0,
          album: fullAlbumData, // Use the complete album data from API
        };

        // Add first track to recent tracks
        const recentTrack = {
          track: {
            name: firstTrack.title || firstTrack.name,
            url: firstTrack.url,
            trackIndex: 1,
          },
          contributor: {
            id: fullAlbumData.id,
            givenName: contributor?.givenName || t('common.unknownArtist'),
            thumbnailUrl:
              contributor?.thumbnailUrl || placeHolderData.contributorThumbnail,
          },
          albumArtUrl:
            fullAlbumData.albumArtUrl ||
            contributor?.thumbnailUrl ||
            placeHolderData.albumCover,
          timestamp: Date.now(),
        };
        addRecentTrack(recentTrack);
        setActivePlaylist(playlist);
        setActivePopup('musicplayer');
      } catch (error) {
        console.error('Error loading album data:', error);
        // Could show a toast error message here
      }
    },
    [loadAlbumById, setActivePlaylist, setActivePopup, addRecentTrack],
  );

  const playTrack = useCallback(
    async (track: any) => {
      try {
        // For tracks, we need to find the album that contains this track
        // Since favorite track data doesn't include album ID, we'll create a single-track playlist
        const contributor =
          track.contributors && track.contributors.length > 0
            ? track.contributors[0]
            : null;
        const trackInfo = await getTrackById(track.id);

        if (trackInfo) {
          // Create a track object with the new locale-aware structure
          const trackWithUrls = {
            id: track.id,
            name: track.title || track.name,
            duration: 0, // Duration not available in favorite track data
            isLiked: true,
            // Use the new urls structure from the API response, with fallback for backward compatibility
            urls: trackInfo.audioUrl?.urls || [],
            // Keep the old url property as fallback if urls array is empty
            url:
              trackInfo.audioUrl?.urls && trackInfo.audioUrl.urls.length > 0
                ? undefined
                : trackInfo.audioUrl?.path +
                  '/' +
                  trackInfo.audioUrl?.fileName +
                  '.mp3',
          };

          // Create a single-track album for this favorite track
          const singleTrackAlbum = {
            id: trackInfo?.album?.id,
            albumName: track.title,
            albumArtUrl: track.albumArtUrl,
            contributors: track.contributors,
            tracks: [trackWithUrls],
            category: 'favorites',
            likes: 0,
            isLiked: true,
            upc: '',
            pk: '',
            description: '',
            genre: [],
            type: 'favorite',
          };

          const playlist = {
            currentIndex: 0,
            album: singleTrackAlbum,
          };

          // Add to recent tracks
          const recentTrack = {
            track: {
              name: track.title || track.name,
              url: getTrackUrlForLocale(trackWithUrls, preferredLocale),
              trackIndex: 1,
            },
            contributor: {
              id: track.id,
              givenName: contributor?.givenName || t('common.unknownArtist'),
              thumbnailUrl:
                contributor?.thumbnailUrl ||
                placeHolderData.contributorThumbnail,
            },
            albumArtUrl:
              track.albumArtUrl ||
              contributor?.thumbnailUrl ||
              placeHolderData.albumCover,
            timestamp: Date.now(),
          };
          setActivePopup('musicplayer');
          addRecentTrack(recentTrack);
          setActivePlaylist(playlist);
        }
      } catch (error) {
        console.error('Error playing track:', error);
        // Could show a toast error message here
      }
    },
    [setActivePlaylist, addRecentTrack],
  );

  // Get filtered data based on active tab using category-based hooks
  const getFilteredData = () => {
    switch (activeTab) {
      case 'music':
        return {
          albums: musicFavorites.items,
          tracks: [],
          contributors: [],
          companions: [],
          category: 'music',
        };
      case 'serenity':
        return {
          albums: serenityFavorites.items,
          tracks: [],
          contributors: [],
          companions: [],
          category: 'serenity',
        };
      case 'book':
        return {
          albums: wisdomFavorites.items,
          tracks: [],
          contributors: [],
          companions: [],
          category: 'book',
        };
      case 'tracks':
        return {
          albums: [],
          tracks: tracksFavorites.items,
          contributors: [],
          companions: [],
          category: 'tracks',
        };
      case 'contributors':
        return {
          albums: [],
          tracks: [],
          contributors: contributorsFavorites.items,
          companions: [],
          category: 'contributors',
        };
      case 'companions':
        return {
          albums: [],
          tracks: [],
          contributors: [],
          companions: companionsFavorites.items,
          category: 'companions',
        };
      default: // 'all'
        return {
          albums: [
            ...musicFavorites.items,
            ...serenityFavorites.items,
            ...wisdomFavorites.items,
          ],
          tracks: tracksFavorites.items,
          contributors: contributorsFavorites.items,
          companions: companionsFavorites.items,
          category: 'all',
        };
    }
  };

  const { albums, tracks, contributors, companions } = getFilteredData();

  // Use totals from API instead of calculating on frontend
  const musicCount = musicFavorites.total;
  const serenityCount = serenityFavorites.total;
  const wisdomCount = wisdomFavorites.total;
  const tracksCount = tracksFavorites.total;
  const contributorsCount = contributorsFavorites.total;
  const companionsCount = companionsFavorites.total;
  const hasNoData =
    albums.length === 0 &&
    tracks.length === 0 &&
    contributors.length === 0 &&
    companions.length === 0;
  const isLoading =
    initialLoading ||
    (musicFavorites.loading && musicFavorites.items.length === 0) ||
    (serenityFavorites.loading && serenityFavorites.items.length === 0) ||
    (wisdomFavorites.loading && wisdomFavorites.items.length === 0) ||
    (tracksFavorites.loading && tracksFavorites.items.length === 0) ||
    (contributorsFavorites.loading &&
      contributorsFavorites.items.length === 0) ||
    (companionsFavorites.loading && companionsFavorites.items.length === 0);

  // Filter tabs configuration
  const filterTabs = [
    { id: 'all' as FilterTab, label: t('favorites.tabAll') },
    { id: 'music' as FilterTab, label: t('favorites.tabMusic') },
    { id: 'serenity' as FilterTab, label: t('favorites.tabSerenity') },
    { id: 'book' as FilterTab, label: t('favorites.tabAudiobooks') },
    { id: 'tracks' as FilterTab, label: t('favorites.tabTracks') },
    { id: 'contributors' as FilterTab, label: t('favorites.tabContributors') },
    { id: 'companions' as FilterTab, label: t('favorites.tabCompanions') },
  ];

  return (
    <div className="flex flex-col h-full pt-0 sm:pt-[10px]">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6">
        <div className="flex flex-col">
          <h1 className="text-[32px] sm:text-[40px] font-normal leading-[140%] text-left text-[#fcfcfc] font-['DM_Serif_Text']">
            {t('favorites.title')}
          </h1>
          <div className="text-[14px] font-normal leading-[140%] text-left text-white/80 pb-4 font-[NapsterSans]">
            {t('favorites.subtitle')}
          </div>
        </div>

        {/* Banner for old music users */}
        {/*{user?.profile?.isOldMusicUser === true && (
          <div className="mb-4 rounded-2xl bg-white/10 text-white hover:bg-white/20 p-4 flex items-center justify-between">
            <div className="text-[#fcfcfc] font-[NapsterSans] text-sm sm:text-base">
              Missing your old favorites?
            </div>
            <button
              onClick={() => navigate('/oldplaylists')}
              className="px-4 py-2 bg-white text-black rounded-full text-sm font-medium hover:bg-white/90 transition-colors font-[NapsterSans]">
              Learn more
            </button>
          </div>
        )}*/}

        <div className="flex flex-wrap gap-2 mb-4">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-black'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}>
              {tab.label}
              {tab.id !== 'all' && (
                <span className="ml-2 text-xs opacity-70">
                  {tab.id === 'music'
                    ? musicCount
                    : tab.id === 'serenity'
                      ? serenityCount
                      : tab.id === 'book'
                        ? wisdomCount
                        : tab.id === 'tracks'
                          ? tracksCount
                          : tab.id === 'contributors'
                            ? contributorsCount
                            : tab.id === 'companions'
                              ? companionsCount
                              : 0}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto na-scroll-container">
        <div className="px-4 sm:px-6 pb-[125px]">
          {/* Loading State - Skeleton Grid */}
          {isLoading && (
            <div className="space-y-8">
              {/* Music Skeletons - Show when loading 'all' or 'music' tab */}
              {(activeTab === 'all' || activeTab === 'music') && (
                <div>
                  {activeTab === 'all' && (
                    <div className="h-6 bg-gray-700 rounded w-32 mb-4 animate-pulse"></div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, index) => (
                      <div
                        key={`loading-music-${index}`}
                        className="flex flex-col cursor-pointer">
                        <div className="relative w-full h-[520px] rounded-[16px] bg-gray-800 animate-pulse overflow-hidden shadow-[0_4px_16px_0_rgba(0,0,0,0.40)]">
                          {/* Background skeleton */}
                          <div className="absolute top-0 left-0 w-full h-full bg-gray-700"></div>

                          {/* Gradient Overlay */}
                          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_34.69%,rgba(0,0,0,0.9)_100%)] z-[99]" />

                          {/* Badge skeleton */}
                          <div className="absolute top-[16px] left-[16px] z-[100] w-[78px] h-[40px] bg-gray-600 rounded-full"></div>

                          {/* Album cover with vinyl skeleton */}
                          <div className="absolute bottom-[120px] left-[20px] z-[50] overflow-hidden w-[172px] h-[132px]">
                            <div className="absolute top-0 left-[35px] w-[73px] h-[73px] md:w-[132px] md:h-[132px] rounded-full bg-gray-600 z-[5]"></div>
                            <div className="absolute left-0 top-0 w-[73px] h-[73px] md:w-[132px] md:h-[132px] bg-gray-600 rounded-[8px] z-[10]"></div>
                          </div>

                          {/* Bottom Section skeleton */}
                          <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-3 w-full">
                            <div className="h-[28px] bg-gray-600 rounded w-4/5"></div>
                            <div className="flex items-center gap-2">
                              <div className="w-[56px] h-[56px] rounded-full bg-gray-600 flex-shrink-0"></div>
                              <div className="h-[18px] bg-gray-600 rounded w-32"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Serenity Skeletons - Show when loading 'all' or 'serenity' tab */}
              {(activeTab === 'all' || activeTab === 'serenity') && (
                <div>
                  {activeTab === 'all' && (
                    <div className="h-6 bg-gray-700 rounded w-48 mb-4 animate-pulse"></div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, index) => (
                      <div
                        key={`loading-serenity-${index}`}
                        className="flex flex-col cursor-pointer">
                        <div className="relative w-full h-[520px] rounded-[16px] bg-gray-800 animate-pulse overflow-hidden shadow-[0_4px_16px_0_rgba(0,0,0,0.40)]">
                          <div className="absolute top-0 left-0 w-full h-full bg-gray-700"></div>
                          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_34.69%,rgba(0,0,0,0.9)_100%)] z-[99]" />
                          <div className="absolute top-[16px] left-[16px] z-[100] w-[90px] h-[40px] bg-gray-600 rounded-full"></div>
                          <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-3 w-full">
                            <div className="h-[28px] bg-gray-600 rounded w-4/5"></div>
                            <div className="flex items-center gap-2">
                              <div className="w-[56px] h-[56px] rounded-full bg-gray-600 flex-shrink-0"></div>
                              <div className="h-[18px] bg-gray-600 rounded w-32"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Audiobook Skeletons - Show when loading 'all' or 'book' tab */}
              {(activeTab === 'all' || activeTab === 'book') && (
                <div>
                  {activeTab === 'all' && (
                    <div className="h-6 bg-gray-700 rounded w-32 mb-4 animate-pulse"></div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, index) => (
                      <div
                        key={`loading-audiobooks-${index}`}
                        className="flex flex-col cursor-pointer">
                        <div
                          className="relative w-full h-[520px] rounded-[16px] animate-pulse overflow-hidden shadow-[0_4px_16px_0_rgba(0,0,0,0.40)]"
                          style={{
                            background:
                              'linear-gradient(180deg, #3B4758 0%, #000000 100%)',
                          }}>
                          <div className="absolute top-[16px] left-[16px] z-[100] w-[110px] h-[40px] bg-gray-600/50 rounded-full"></div>
                          <div className="absolute inset-0 flex top-[80px] justify-center z-[50]">
                            <div className="w-[185px] h-[275.1px] rounded-[8px] bg-gray-600/50 shadow-lg"></div>
                          </div>
                          <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-3 w-full">
                            <div className="h-[28px] bg-gray-600/50 rounded w-4/5"></div>
                            <div className="flex items-center gap-2">
                              <div className="w-[56px] h-[56px] rounded-full bg-gray-600/50 flex-shrink-0"></div>
                              <div className="h-[18px] bg-gray-600/50 rounded w-32"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tracks Skeletons - Show when loading 'all' or 'tracks' tab */}
              {(activeTab === 'all' || activeTab === 'tracks') && (
                <div>
                  {activeTab === 'all' && (
                    <div className="h-6 bg-gray-700 rounded w-32 mb-4 animate-pulse"></div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[...Array(6)].map((_, index) => (
                      <div
                        key={`loading-track-${index}`}
                        className="flex gap-3 items-center p-3 rounded-lg animate-pulse">
                        <div className="w-12 h-12 rounded-md bg-gray-700 flex-shrink-0"></div>
                        <div className="flex-1 min-w-0">
                          <div className="h-4 bg-gray-700 rounded mb-2 w-3/4"></div>
                          <div className="h-3 bg-gray-600 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contributors Skeletons - Show when loading 'all' or 'contributors' tab */}
              {(activeTab === 'all' || activeTab === 'contributors') && (
                <div>
                  {activeTab === 'all' && (
                    <div className="h-6 bg-gray-700 rounded w-40 mb-4 animate-pulse"></div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[...Array(8)].map((_, index) => (
                      <div
                        key={`loading-contributor-${index}`}
                        className="flex flex-col animate-pulse">
                        <div className="w-full h-[520px] rounded-[16px] bg-gray-700"></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Content when not loading */}
          {!isLoading && (
            <>
              {/* Empty State */}
              {hasNoData ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="mb-4 opacity-50">
                    <FavoriteIcon width={48} height={48} fill="white" />
                  </div>
                  <h3 className="text-white text-lg font-medium mb-2">
                    {t('favorites.noFavoritesYet')}
                  </h3>
                  <p className="text-white/60 text-sm text-center max-w-sm">
                    {t('favorites.noFavoritesMessage')}
                  </p>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Render albums by category when viewing "All", or filtered albums for specific categories */}
                  {activeTab === 'all' ? (
                    <>
                      {/* Music Albums */}
                      {musicFavorites.items.length > 0 && (
                        <div>
                          <h3 className="text-white text-xl text-left font-bold mb-4">
                            {t('favorites.musicCount', { count: musicCount })}
                          </h3>
                          <div className="responsive-grid gap-6">
                            {musicFavorites.items.map(
                              (album: FavoriteAlbumData, index: number) => (
                                <FavoriteAlbumCard
                                  key={`favorite-music-${album.id || index}`}
                                  album={album}
                                  onPlay={playAlbum}
                                  onUnfavorite={(id) =>
                                    handleUnfavorite('album', id)
                                  }
                                />
                              ),
                            )}
                          </div>

                          {/* Load More Button for Music Albums */}
                          {musicFavorites.hasMore && (
                            <div className="flex justify-center mt-6">
                              <button
                                onClick={() => musicFavorites.loadMore()}
                                disabled={musicFavorites.loading}
                                className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                                {musicFavorites.loading ? (
                                  <div className="flex items-center gap-2">
                                    <NapsterSpinner size="small" />
                                    {t('common.loading')}
                                  </div>
                                ) : (
                                  t('favorites.loadMoreMusic')
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Serenity & Wellness Albums */}
                      {serenityFavorites.items.length > 0 && (
                        <div>
                          <h3 className="text-white text-lg text-left font-bold mb-4">
                            {t('favorites.serenityCount', { count: serenityCount })}
                          </h3>
                          <div className="responsive-grid gap-6">
                            {serenityFavorites.items.map(
                              (album: FavoriteAlbumData, index: number) => (
                                <FavoriteAlbumCard
                                  key={`favorite-serenity-${album.id || index}`}
                                  album={album}
                                  onPlay={playAlbum}
                                  onUnfavorite={(id) =>
                                    handleUnfavorite('album', id)
                                  }
                                />
                              ),
                            )}
                          </div>

                          {/* Load More Button for Serenity Albums */}
                          {serenityFavorites.hasMore && (
                            <div className="flex justify-center mt-6">
                              <button
                                onClick={() => serenityFavorites.loadMore()}
                                disabled={serenityFavorites.loading}
                                className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                                {serenityFavorites.loading ? (
                                  <div className="flex items-center gap-2">
                                    <NapsterSpinner size="small" />
                                    {t('common.loading')}
                                  </div>
                                ) : (
                                  t('favorites.loadMoreSerenity')
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Audiobook Albums */}
                      {wisdomFavorites.items.length > 0 && (
                        <div>
                          <h3 className="text-white text-lg text-left font-bold mb-4">
                            {t('favorites.audiobooksCount', { count: wisdomCount })}
                          </h3>
                          <div className="responsive-grid gap-6">
                            {wisdomFavorites.items.map(
                              (album: FavoriteAlbumData, index: number) => (
                                <FavoriteAlbumCard
                                  key={`favorite-audiobooks-${
                                    album.id || index
                                  }`}
                                  album={album}
                                  onPlay={playAlbum}
                                  onUnfavorite={(id) =>
                                    handleUnfavorite('album', id)
                                  }
                                />
                              ),
                            )}
                          </div>

                          {/* Load More Button for Audiobook Albums */}
                          {wisdomFavorites.hasMore && (
                            <div className="flex justify-center mt-6">
                              <button
                                onClick={() => wisdomFavorites.loadMore()}
                                disabled={wisdomFavorites.loading}
                                className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                                {wisdomFavorites.loading ? (
                                  <div className="flex items-center gap-2">
                                    <NapsterSpinner size="small" />
                                    {t('common.loading')}
                                  </div>
                                ) : (
                                  t('favorites.loadMoreAudiobooks')
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    /* Single category view */
                    albums.length > 0 && (
                      <div>
                        <div className="responsive-grid gap-6">
                          {albums.map(
                            (album: FavoriteAlbumData, index: number) => (
                              <FavoriteAlbumCard
                                key={`favorite-album-${album.id || index}`}
                                album={album}
                                onPlay={playAlbum}
                                onUnfavorite={(id) =>
                                  handleUnfavorite('album', id)
                                }
                              />
                            ),
                          )}
                        </div>

                        {/* Load More Button for Single Category Albums */}
                        {((activeTab === 'music' && musicFavorites.hasMore) ||
                          (activeTab === 'serenity' &&
                            serenityFavorites.hasMore) ||
                          (activeTab === 'book' &&
                            wisdomFavorites.hasMore)) && (
                          <div className="flex justify-center mt-6">
                            <button
                              onClick={() => {
                                if (activeTab === 'music')
                                  musicFavorites.loadMore();
                                else if (activeTab === 'serenity')
                                  serenityFavorites.loadMore();
                                else if (activeTab === 'book')
                                  wisdomFavorites.loadMore();
                              }}
                              disabled={
                                (activeTab === 'music' &&
                                  musicFavorites.loading) ||
                                (activeTab === 'serenity' &&
                                  serenityFavorites.loading) ||
                                (activeTab === 'book' &&
                                  wisdomFavorites.loading)
                              }
                              className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                              {(activeTab === 'music' &&
                                musicFavorites.loading) ||
                              (activeTab === 'serenity' &&
                                serenityFavorites.loading) ||
                              (activeTab === 'book' &&
                                wisdomFavorites.loading) ? (
                                <div className="flex items-center gap-2">
                                  <NapsterSpinner size="small" />
                                  {t('common.loading')}
                                </div>
                              ) : (
                                activeTab === 'music'
                                  ? t('favorites.loadMoreMusic')
                                  : activeTab === 'serenity'
                                    ? t('favorites.loadMoreSerenity')
                                    : t('favorites.loadMoreAudiobooks')
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  )}

                  {/* Tracks Section */}
                  {tracks.length > 0 && (
                    <div>
                      {activeTab === 'all' && (
                        <h3 className="text-white text-lg text-left font-bold mb-4">
                          {t('favorites.tracksCount', { count: tracksCount })}
                        </h3>
                      )}
                      {/* 2 columns on desktop, 1 column on mobile */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {tracks.map((track: any, index: number) => (
                          <div
                            key={`favorite-track-${track.id || index}`}
                            className="flex gap-3 items-center p-3 rounded-lg hover:bg-white/5 transition-colors duration-200 cursor-pointer group"
                            onClick={() => playTrack(track)}>
                            {/* Left side - Square album cover */}
                            <div className="relative flex-shrink-0">
                              <img
                                src={
                                  track.albumArtUrl ||
                                  placeHolderData.albumCover
                                }
                                alt={track.title}
                                className="w-12 h-12 rounded-md object-cover shadow-[0_4px_16px_0_rgba(0,0,0,0.4)]"
                              />
                            </div>

                            {/* Right side - Track info */}
                            <div className="flex-1 min-w-0 font-[NapsterSans]">
                              <div className="text-white text-sm text-left font-bold leading-[130%] mb-1 line-clamp-1">
                                {track.title}
                              </div>
                              <div className="flex items-center gap-1.5">
                                {track.contributors?.[0]?.thumbnailUrl && (
                                  <img
                                    src={track.contributors[0].thumbnailUrl}
                                    alt={track.contributors[0].givenName}
                                    className="w-4 h-4 rounded-full"
                                  />
                                )}
                                <div className="text-white/80 text-xs leading-[130%] line-clamp-1">
                                  {track.contributors?.[0]?.givenName ||
                                    t('common.unknownArtist')}
                                </div>
                              </div>
                            </div>

                            {/* Right side - Action buttons */}
                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              {/* Play Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  playTrack(track);
                                }}
                                className="flex items-center justify-center w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors duration-200">
                                <PlayIcon width={14} height={14} />
                              </button>

                              {/* Favorite Button (Remove from favorites) */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnfavorite('track', track.id);
                                }}
                                disabled={unfavoritingItems.has(track.id)}
                                className="flex items-center justify-center w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors duration-200 disabled:opacity-50">
                                {unfavoritingItems.has(track.id) ? (
                                  <NapsterSpinner size="small" />
                                ) : (
                                  <Likedicon width={14} height={15} />
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Load More Button for Tracks */}
                      {tracksFavorites.hasMore && (
                        <div className="flex justify-center mt-6">
                          <button
                            onClick={() => tracksFavorites.loadMore()}
                            disabled={tracksFavorites.loading}
                            className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                            {tracksFavorites.loading ? (
                              <div className="flex items-center gap-2">
                                <NapsterSpinner size="small" />
                                {t('common.loading')}
                              </div>
                            ) : (
                              t('favorites.loadMoreTracks')
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Contributors Section */}
                  {contributors.length > 0 && (
                    <div>
                      {activeTab === 'all' && (
                        <h3 className="text-white text-lg text-left font-bold mb-4">
                          {t('favorites.contributorsCount', { count: contributorsCount })}
                        </h3>
                      )}
                      <div className="responsive-grid gap-6">
                        {contributors.map((contributor: any, index: number) => (
                          <div
                            key={`favorite-contributor-${
                              contributor.id || index
                            }`}
                            className="flex flex-col">
                            <ContributorCard
                              contributor={contributor}
                              videoUrl={contributor?.videoMobileUrl}
                              posterUrl={
                                contributor?.videoMobilePreviewUrl ||
                                contributor?.thumbnailUrl
                              }
                              name={contributor?.name}
                              callToAction={contributor?.call_to_action}
                              firstName={contributor?.firstName}
                              lastName={contributor?.lastName}
                              jobTitle={contributor?.jobTitle}
                              onClick={() =>
                                navigate(`/contributor/${contributor.id}`)
                              }
                              onFavoriteClick={(e) => {
                                e.stopPropagation();
                                handleUnfavorite('contributor', contributor.id);
                              }}
                              showFavoriteButton={true}
                              isLoading={unfavoritingItems.has(contributor.id)}
                              isFavorited={true}
                            />
                          </div>
                        ))}
                      </div>

                      {/* Load More Button for Contributors */}
                      {contributorsFavorites.hasMore && (
                        <div className="flex justify-center mt-6">
                          <button
                            onClick={() => contributorsFavorites.loadMore()}
                            disabled={contributorsFavorites.loading}
                            className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                            {contributorsFavorites.loading ? (
                              <div className="flex items-center gap-2">
                                <NapsterSpinner size="small" />
                                {t('common.loading')}
                              </div>
                            ) : (
                              t('favorites.loadMoreContributors')
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Companions Section */}
                  {companions.length > 0 && (
                    <div>
                      {activeTab === 'all' && (
                        <h3 className="text-white text-lg text-left font-bold mb-4">
                          {t('favorites.companionsCount', { count: companionsCount })}
                        </h3>
                      )}
                      <div className="responsive-grid gap-6">
                        {companions.map((companion: any, index: number) => (
                          <div
                            key={`favorite-companion-${
                              companion.mentorId ||
                              companion.companionId ||
                              index
                            }`}
                            className="flex flex-col">
                            <CompanionCard
                              companion={companion}
                              showFavoriteButton={true}
                              isFavorited={true}
                              isLoading={unfavoritingItems.has(
                                companion.mentorId || companion.companionId,
                              )}
                              onFavoriteClick={(e) => {
                                e.stopPropagation();
                                // Use mentorId as companionId for unfavoriting
                                handleUnfavorite(
                                  'companion',
                                  companion.mentorId || companion.companionId,
                                );
                              }}
                            />
                          </div>
                        ))}
                      </div>

                      {/* Load More Button for Companions */}
                      {companionsFavorites.hasMore && (
                        <div className="flex justify-center mt-6">
                          <button
                            onClick={() => companionsFavorites.loadMore()}
                            disabled={companionsFavorites.loading}
                            className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                            {companionsFavorites.loading ? (
                              <div className="flex items-center gap-2">
                                <NapsterSpinner size="small" />
                                {t('common.loading')}
                              </div>
                            ) : (
                              t('favorites.loadMoreCompanions')
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Infinite Scroll Sentinels and Loading Indicators */}
              <>
                {/* Music Albums Sentinel */}
                {(activeTab === 'all' || activeTab === 'music') &&
                  musicFavorites.items.length > 0 && (
                    <>
                      <div ref={musicFavorites.sentinelRef} className="h-4" />
                      {musicFavorites.loading && (
                        <div className="flex justify-center py-8">
                          <NapsterSpinner />
                        </div>
                      )}
                      {musicFavorites.error && (
                        <div className="flex justify-center py-4">
                          <p className="text-red-400 text-sm">
                            {musicFavorites.error}
                          </p>
                        </div>
                      )}
                      {/* Load More Button for Music */}
                      {musicFavorites.hasMore && !musicFavorites.loading && (
                        <div className="flex justify-center py-4">
                          <button
                            onClick={() => musicFavorites.loadMore()}
                            className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors duration-200">
                            {t('favorites.loadMoreMusic')}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                {/* Serenity Albums Sentinel */}
                {(activeTab === 'all' || activeTab === 'serenity') &&
                  serenityFavorites.items.length > 0 && (
                    <>
                      <div
                        ref={serenityFavorites.sentinelRef}
                        className="h-4"
                      />
                      {serenityFavorites.loading && (
                        <div className="flex justify-center py-8">
                          <NapsterSpinner />
                        </div>
                      )}
                      {serenityFavorites.error && (
                        <div className="flex justify-center py-4">
                          <p className="text-red-400 text-sm">
                            {serenityFavorites.error}
                          </p>
                        </div>
                      )}
                      {/* Load More Button for Serenity */}
                      {serenityFavorites.hasMore &&
                        !serenityFavorites.loading && (
                          <div className="flex justify-center py-4">
                            <button
                              onClick={() => serenityFavorites.loadMore()}
                              className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors duration-200">
                              {t('favorites.loadMoreSerenity')}
                            </button>
                          </div>
                        )}
                    </>
                  )}

                {/* Audiobook Albums Sentinel */}
                {(activeTab === 'all' || activeTab === 'book') &&
                  wisdomFavorites.items.length > 0 && (
                    <>
                      <div ref={wisdomFavorites.sentinelRef} className="h-4" />
                      {wisdomFavorites.loading && (
                        <div className="flex justify-center py-8">
                          <NapsterSpinner />
                        </div>
                      )}
                      {wisdomFavorites.error && (
                        <div className="flex justify-center py-4">
                          <p className="text-red-400 text-sm">
                            {wisdomFavorites.error}
                          </p>
                        </div>
                      )}
                      {/* Load More Button for Audiobook */}
                      {wisdomFavorites.hasMore && !wisdomFavorites.loading && (
                        <div className="flex justify-center py-4">
                          <button
                            onClick={() => wisdomFavorites.loadMore()}
                            className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors duration-200">
                            {t('favorites.loadMoreAudiobooks')}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                {/* Tracks Sentinel - Show when tracks are displayed */}
                {(activeTab === 'all' || activeTab === 'tracks') &&
                  tracksFavorites.items.length > 0 && (
                    <>
                      <div ref={tracksFavorites.sentinelRef} className="h-4" />
                      {tracksFavorites.loading && (
                        <div className="flex justify-center py-8">
                          <NapsterSpinner />
                        </div>
                      )}
                      {tracksFavorites.error && (
                        <div className="flex justify-center py-4">
                          <p className="text-red-400 text-sm">
                            {tracksFavorites.error}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                {/* Contributors Sentinel - Show when contributors are displayed */}
                {(activeTab === 'all' || activeTab === 'contributors') &&
                  contributorsFavorites.items.length > 0 && (
                    <>
                      <div
                        ref={contributorsFavorites.sentinelRef}
                        className="h-4"
                      />
                      {contributorsFavorites.loading && (
                        <div className="flex justify-center py-8">
                          <NapsterSpinner />
                        </div>
                      )}
                      {contributorsFavorites.error && (
                        <div className="flex justify-center py-4">
                          <p className="text-red-400 text-sm">
                            {contributorsFavorites.error}
                          </p>
                        </div>
                      )}
                      {/* Load More Button for Contributors */}
                      {contributorsFavorites.hasMore &&
                        !contributorsFavorites.loading && (
                          <div className="flex justify-center py-4">
                            <button
                              onClick={() => contributorsFavorites.loadMore()}
                              className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors duration-200">
                              {t('favorites.loadMoreContributors')}
                            </button>
                          </div>
                        )}
                    </>
                  )}

                {/* Companions Sentinel - Show when companions are displayed */}
                {(activeTab === 'all' || activeTab === 'companions') &&
                  companionsFavorites.items.length > 0 && (
                    <>
                      <div
                        ref={companionsFavorites.sentinelRef}
                        className="h-4"
                      />
                      {companionsFavorites.loading && (
                        <div className="flex justify-center py-8">
                          <NapsterSpinner />
                        </div>
                      )}
                      {companionsFavorites.error && (
                        <div className="flex justify-center py-4">
                          <p className="text-red-400 text-sm">
                            {companionsFavorites.error}
                          </p>
                        </div>
                      )}
                      {/* Load More Button for Companions */}
                      {companionsFavorites.hasMore &&
                        !companionsFavorites.loading && (
                          <div className="flex justify-center py-4">
                            <button
                              onClick={() => companionsFavorites.loadMore()}
                              className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors duration-200">
                              {t('favorites.loadMoreCompanions')}
                            </button>
                          </div>
                        )}
                    </>
                  )}
              </>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

FavoritesPage.displayName = 'FavoritesPage';
